import React, {PropsWithChildren, useEffect, useRef, useState} from 'react';
import {Tooltip} from 'react-tooltip';
import {createPortal} from 'react-dom';
import 'react-tooltip/dist/react-tooltip.css';

export enum TooltipModifiers {
    TOO_LONG_TITLE = 'c-tooltip--tooLong',
    NOT_LONG_TITLE = 'c-tooltip--notLong',
}

interface TooltipProps {
    id: string;
    className?: string;
    content: JSX.Element | string;
    place?: 'top' | 'right' | 'bottom' | 'left';
    disable?: boolean;
    tooltipClassModifiers?: TooltipModifiers;
    delayShow?: number;
    hide?: boolean;
    color?: string;
}

function TooltipWrapper(props: PropsWithChildren<TooltipProps>): JSX.Element {
    const {
        id,
        className = '',
        children,
        content,
        place = 'top',
        disable = false,
        tooltipClassModifiers = '',
        delayShow,
        hide = false,
        color,
    } = props;
    const tooltipRef = useRef<HTMLDivElement>(null);

    const [tooltipOpen, setTooltipOpen] = useState(false);

    const handleToggleTooltip = (e: React.TouchEvent): void => {
        e.stopPropagation();
        setTooltipOpen((prev) => !prev);
    };

    const handleTouchOutside = (e: TouchEvent): void => {
        if (tooltipRef.current && !tooltipRef.current.contains(e.target as Node)) {
            setTooltipOpen(false);
        }
    };

    useEffect(() => {
        if (tooltipOpen) {
            document.addEventListener('touchstart', handleTouchOutside);
        } else {
            document.removeEventListener('touchstart', handleTouchOutside);
        }
        return () => {
            document.removeEventListener('touchstart', handleTouchOutside);
        };
    }, [tooltipOpen]);

    if (!hide) {
        return (
            <div className={className} ref={tooltipRef}>
                <div data-tooltip-id={id} onTouchStart={handleToggleTooltip} style={{color}}>
                    {children}
                </div>
                {createPortal(
                    <Tooltip
                        className={`c-tooltip ${tooltipClassModifiers}`}
                        style={{background: TOOLTIP_BACKGROUND_COLOR}}
                        id={id}
                        place={place}
                        hidden={disable}
                        delayShow={delayShow}
                        isOpen={tooltipOpen}
                        setIsOpen={setTooltipOpen}
                    >
                        {content}
                    </Tooltip>,
                    document.body
                )}
            </div>
        );
    }
    return <div className={className}>{children}</div>;
}

const TOOLTIP_BACKGROUND_COLOR = '#454545';

export default TooltipWrapper;
